import { useAuthContext } from '@/components/Providers/AuthProvider';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { CustomLink } from '@/types/Common';
import { MetaBaseStrapi } from '@/types/MetaData';

import Dropdown from '../../DropdownMenu';
import MenuBar from '../MenuBar';

const defaultIconFont = '!v2-text-[1rem] v2-font-circular-std-bold';
const navItemClassName = '!v2-text-black !v2-font-normal !v2-text-xs !v2-font-sweet-sans-pro';

export default function BackofficeDesktopMenu({
  dogCategories,
  dogIssues,
  catCategories,
  catIssues,
  ourStories,
  aboutUs,
  blogUrl,
  ingredientUrl,
  ezShipManagerPath,
  petProfilePath,
  businessReports,
  commissions,
  customerReports,
  events,
  petProfileAdmin,
  proTools,
  support,
  tools,
  dashboardUrl,
}: {
  dogCategories: MetaBaseStrapi[];
  dogIssues: MetaBaseStrapi[];
  catCategories: MetaBaseStrapi[];
  catIssues: MetaBaseStrapi[];
  ourStories: CustomLink[];
  aboutUs: CustomLink[];

  blogUrl: string;
  ingredientUrl: string;
  petProfilePath: string;
  ezShipManagerPath: string;
  tools: CustomLink[];
  commissions: CustomLink[];
  businessReports: CustomLink[];
  dashboardUrl: string;
  customerReports: CustomLink[];
  petProfileAdmin: CustomLink[];
  events: CustomLink[];
  proTools: CustomLink[];
  support: CustomLink[];
}) {
  const { isAdmin, customPages } = useAuthContext();
  const { webalias, generateURL } = useWebAlias();
  const { customer } = useAuthContext();

  return (
    <>
      <Dropdown label="Main Menu">
        <Dropdown.SingleColumnLayout className="!v2-px-0">
          <Dropdown.SingleColumnLayout.SubMenu label="Shop Dogs">
            {props => (
              <Dropdown.TwoColumnLayout {...props}>
                <Dropdown.TwoColumnLayout.Column headline="Shop by products">
                  {dogCategories.map(category => (
                    <Dropdown.TwoColumnLayout.Column.ColumnItem
                      key={category.Name}
                      iconClassName={cn(defaultIconFont, {
                        '!v2-text-[19px]': category.id === Infinity,
                      })}
                      label={category.Name}
                      className={cn('v2-pb-2', {
                        'v2-text-primary': category.id === Infinity,
                      })}
                      labelClassName={cn(navItemClassName, {
                        '!v2-font-normal !v2-text-primary-900': category.id === Infinity,
                      })}
                      href={generateApproperiateURL(
                        `/products/${category.id === Infinity ? '' : category.slug || category.id}`,
                        webalias
                      )}
                    />
                  ))}
                </Dropdown.TwoColumnLayout.Column>

                <Dropdown.TwoColumnLayout.Column headline="Shop by Issues">
                  {dogIssues.map(issue => (
                    <Dropdown.TwoColumnLayout.Column.ColumnItem
                      key={issue.Name}
                      iconClassName={cn(defaultIconFont, {
                        '!v2-text-[19px]': issue.id === Infinity,
                      })}
                      label={issue.Name}
                      className={cn('v2-pb-2', {
                        'v2-text-primary': issue.id === Infinity,
                      })}
                      labelClassName={cn(navItemClassName, {
                        '!v2-font-normal !v2-text-primary-900': issue.id === Infinity,
                      })}
                      href={generateApproperiateURL(
                        `/products/${issue.id === Infinity ? '' : issue.slug || issue.id}`,
                        webalias
                      )}
                    />
                  ))}
                </Dropdown.TwoColumnLayout.Column>
              </Dropdown.TwoColumnLayout>
            )}
          </Dropdown.SingleColumnLayout.SubMenu>
          <Dropdown.SingleColumnLayout.SubMenu label="Shop Cats">
            {props => (
              <Dropdown.TwoColumnLayout {...props}>
                <Dropdown.TwoColumnLayout.Column headline="Shop by products">
                  {catCategories.map(category => (
                    <Dropdown.TwoColumnLayout.Column.ColumnItem
                      key={category.Name}
                      iconClassName={cn(defaultIconFont, {
                        '!v2-text-[19px]': category.id === Infinity,
                      })}
                      label={category.Name}
                      className={cn('v2-pb-2', {
                        'v2-text-primary': category.id === Infinity,
                      })}
                      labelClassName={cn(navItemClassName, {
                        '!v2-font-normal !v2-text-primary-900': category.id === Infinity,
                      })}
                      href={generateApproperiateURL(
                        `/products/${category.id === Infinity ? '' : category.slug || category.id}`,
                        webalias
                      )}
                    />
                  ))}
                </Dropdown.TwoColumnLayout.Column>

                <Dropdown.TwoColumnLayout.Column headline="Shop by Issues">
                  {catIssues.map(issue => (
                    <Dropdown.TwoColumnLayout.Column.ColumnItem
                      key={issue.Name}
                      iconClassName={cn(defaultIconFont, {
                        '!v2-text-[19px]': issue.id === Infinity,
                      })}
                      label={issue.Name}
                      className={cn('v2-pb-2', {
                        'v2-text-primary': issue.id === Infinity,
                      })}
                      labelClassName={cn(navItemClassName, {
                        '!v2-font-normal !v2-text-primary-900': issue.id === Infinity,
                      })}
                      href={generateApproperiateURL(
                        `/products/${issue.id === Infinity ? '' : issue.slug || issue.id}`,
                        webalias
                      )}
                    />
                  ))}
                </Dropdown.TwoColumnLayout.Column>
              </Dropdown.TwoColumnLayout>
            )}
          </Dropdown.SingleColumnLayout.SubMenu>
          <Dropdown.SingleColumnLayout.SubMenu label="About Us">
            {props => (
              <Dropdown.SingleColumnLayout {...props}>
                {aboutUs.map(({ link, name }) => (
                  <Dropdown.SingleColumnLayout.ColumnItem
                    key={name}
                    href={link}
                    as={'a'}
                    label={name}
                    labelClassName={navItemClassName}
                    className="v2-pb-2"
                  />
                ))}
              </Dropdown.SingleColumnLayout>
            )}
          </Dropdown.SingleColumnLayout.SubMenu>
          <Dropdown.SingleColumnLayout.SubMenu label="Resources" className="v2-relative">
            {props => (
              <Dropdown.SingleColumnLayout {...props}>
                <Dropdown.SingleColumnLayout.ColumnItem
                  as="a"
                  key="blog"
                  label="Blog"
                  href={blogUrl}
                  labelClassName={navItemClassName}
                  className="v2-pb-2"
                />
                <Dropdown.SingleColumnLayout.ColumnItem
                  as="a"
                  key="ingredient-101"
                  label="Ingredients 101"
                  href={ingredientUrl}
                  labelClassName={navItemClassName}
                  className="v2-pb-2"
                />
              </Dropdown.SingleColumnLayout>
            )}
          </Dropdown.SingleColumnLayout.SubMenu>
          <li className="v2-py-1 v2-px-6">
            <MenuBar.Link
              href={petProfilePath}
              as={'a'}
              target="_blank"
              className={'!v2-text-sm'}
              onClick={() => window.open(petProfilePath, '_blank')}
            >
              Pet Quiz
            </MenuBar.Link>{' '}
          </li>
        </Dropdown.SingleColumnLayout>
      </Dropdown>
      <MenuBar.Link href={dashboardUrl} as={'a'}>
        Dashboard
      </MenuBar.Link>

      <Dropdown label="My Recommendations">
        <Dropdown.SingleColumnLayout>
          <>
            {(customPages || [])?.map(({ name, customPageID }) => (
              <Dropdown.SingleColumnLayout.ColumnItem
                key={customPageID}
                href={`/customizedrecommendation/edit/${customPageID}`}
                label={name}
                as={'a'}
                className="v2-pb-2"
                labelClassName={cn(navItemClassName, 'px-')}
              />
            ))}
          </>
          <hr className="v2-mt-3 v2-mb-5 v2-border v2-border-t-[#efdfbd]" />
          <Dropdown.SingleColumnLayout.ColumnItem
            href={`/customizedrecommendation`}
            label={'Create Custom Page'}
            as={'a'}
            className="v2-pb-2"
            labelClassName={cn(navItemClassName, 'px-')}
          />
        </Dropdown.SingleColumnLayout>
      </Dropdown>

      <Dropdown label="Reports">
        <Dropdown.TwoColumnLayout>
          <Dropdown.TwoColumnLayout.Column headline="Business Reports">
            {businessReports.map(({ name, link, onClick }) => (
              <Dropdown.TwoColumnLayout.Column.ColumnItem
                key={name}
                label={name}
                href={link}
                as={'a'}
                onClick={onClick}
                labelClassName={navItemClassName}
                className="v2-pb-2"
              />
            ))}
          </Dropdown.TwoColumnLayout.Column>
          <Dropdown.TwoColumnLayout.Column headline="Customer Reports">
            {customerReports.map(({ name, link, onClick }) => (
              <Dropdown.TwoColumnLayout.Column.ColumnItem
                key={name}
                label={name}
                href={link}
                as={'a'}
                onClick={onClick}
                labelClassName={navItemClassName}
                className="v2-pb-2"
              />
            ))}
          </Dropdown.TwoColumnLayout.Column>
        </Dropdown.TwoColumnLayout>
      </Dropdown>
      <Dropdown label="Commissions">
        <Dropdown.SingleColumnLayout>
          {commissions.map(({ name, link, target: _target, onClick }) => (
            <Dropdown.SingleColumnLayout.ColumnItem
              label={name}
              href={link}
              as="a"
              {...(_target ? { target: _target || '_blank' } : {})}
              key={name}
              onClick={onClick}
              labelClassName={navItemClassName}
              className="v2-pb-2"
            />
          ))}
        </Dropdown.SingleColumnLayout>
      </Dropdown>
      <Dropdown label="Events">
        <Dropdown.SingleColumnLayout>
          {events.map(({ name, link, target: _target, onClick }) => (
            <Dropdown.SingleColumnLayout.ColumnItem
              label={name}
              href={link}
              as="a"
              {...(_target ? { target: _target || '_blank' } : {})}
              labelClassName={navItemClassName}
              onClick={onClick}
              className="v2-pb-2"
              key={name}
            />
          ))}
        </Dropdown.SingleColumnLayout>
      </Dropdown>
      <Dropdown label="Pro Tools">
        <Dropdown.SingleColumnLayout>
          {proTools.map(({ name, link, target: _target, as, onClick }) => (
            <Dropdown.SingleColumnLayout.ColumnItem
              label={name}
              href={link}
              as="a"
              {...(_target ? { target: _target || '_blank' } : {})}
              key={name}
              labelClassName={navItemClassName}
              onClick={onClick}
              className="v2-pb-2"
            />
          ))}
        </Dropdown.SingleColumnLayout>
      </Dropdown>
      <Dropdown label="Support">
        <Dropdown.SingleColumnLayout>
          {support.map(({ name, link, target: _target, onClick }) => (
            <Dropdown.SingleColumnLayout.ColumnItem
              label={name}
              href={link}
              as="a"
              {...(_target ? { target: _target || '_blank' } : {})}
              key={name}
              onClick={onClick}
              labelClassName={navItemClassName}
              className="v2-pb-2"
            />
          ))}
        </Dropdown.SingleColumnLayout>
      </Dropdown>
      {isAdmin && (
        <Dropdown label="Tools">
          <Dropdown.SingleColumnLayout className="v2-grid v2-grid-cols-2 v2-gap-x-3">
            {tools.map(({ name, link, target: _target, onClick }) => (
              <Dropdown.SingleColumnLayout.ColumnItem
                label={name}
                href={link}
                as="a"
                key={name}
                {...(_target ? { target: _target || '_blank' } : {})}
                labelClassName={navItemClassName}
                onClick={onClick}
                className="v2-pb-2"
              />
            ))}
          </Dropdown.SingleColumnLayout>
        </Dropdown>
      )}
      {/* {isAdmin && (
        <Dropdown label="PetProfile Admin">
          <Dropdown.SingleColumnLayout>
            {petProfileAdmin.map(({ name, link, onClick }) => (
              <Dropdown.SingleColumnLayout.ColumnItem
                label={name}
                href={link}
                as={'a'}
                key={name}
                labelClassName={navItemClassName}
                onClick={onClick}
                className="v2-pb-2"
              />
            ))}
          </Dropdown.SingleColumnLayout>
        </Dropdown>
      )} */}
      <MenuBar.Link href={ezShipManagerPath}>EZ Ship Manager</MenuBar.Link>
    </>
  );
}
